import { Button, Flex, Input, Spin } from "antd";
import type { UploadFile } from "antd";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import logApis from "@/apis/logBook";
import { UserContext } from "@/layout/context/SettingsContext";
import { DEFAULT_PAGE_SIZE } from "@/styles/STableLayout";
import { WFile } from "@/types/file";
import { Log } from "@/types/logBook";
import { LogWrap } from "../../style";
import LogAttachments, { LogAttachmentUpload } from "./LogAttachments";
import type { LogAttachmentUploadHandle } from "./LogAttachments";
import { createLogAttachmentDocument } from "./LogAttachments/model";
import LogItem from "./LogItem";

interface ActiveReplyAction {
  targetLogId: string;
  targetUserName: string;
  targetContent: string;
}

interface DetailModalProps {
  logBookId?: string;
}

const MAX_LOG_CONTENT_LENGTH = 3000;

const getActionTargetUserName = (log: Log) => {
  if (log.createdBy === "SYSTEM") {
    return "SYSTEM";
  }

  if (!log.creator) {
    return "-";
  }

  return log.creator.name1 || log.creator.name2 || log.creator.mobile || "-";
};

const LogHome: FC<DetailModalProps> = ({ logBookId }) => {
  const { t } = useTranslation();
  const take = DEFAULT_PAGE_SIZE;
  const [loading, setLoading] = useState<boolean>(false);
  const [addLoading, setAddLoading] = useState<boolean>(false);
  const [content, setContent] = useState<string>("");
  const [attachmentFiles, setAttachmentFiles] = useState<UploadFile<WFile>[]>(
    [],
  );
  const [logs, setLogs] = useState<Array<Log>>([]);
  const [offset, setOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [activeAction, setActiveAction] = useState<ActiveReplyAction | undefined>(
    undefined,
  );
  const [expandedReplyLogIds, setExpandedReplyLogIds] = useState<
    Record<string, boolean>
  >({});
  const [[user]] = useContext(UserContext);

  const attachmentUploadRef = useRef<LogAttachmentUploadHandle>(null);
  const lastLogRef = useRef<HTMLDivElement | null>(null);
  const lastSeenLogRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setAddLoading(false);
    setContent("");
    setAttachmentFiles([]);
    setOffset(0);
    setHasMore(true);
    setActiveAction(undefined);
    setExpandedReplyLogIds({});
    getLogList(0, true);
  }, [logBookId]);

  const getLogList = (newOffset: number, resetList = false) => {
    if (!logBookId) return;

    if (!resetList) {
      const element = document.getElementById(`log-${logs[0]?.id}`);
      if (element instanceof HTMLDivElement) {
        lastSeenLogRef.current = element;
      }
    }

    setLoading(true);
    logApis
      .logBookLogsFetch(logBookId, {
        pagination: { take, offset: newOffset, sort: 1 },
      })
      .then((res) => {
        const newLogs = res.body ?? [];
        if (newLogs.length < take) {
          setHasMore(false);
        }

        flushSync(() => {
          setLogs((prevLogs) =>
            newOffset === 0 || resetList ? newLogs : [...newLogs, ...prevLogs],
          );
          setOffset(newOffset + take);
        });

        if (newOffset === 0 || resetList) {
          setTimeout(() => {
            if (lastLogRef.current) {
              lastLogRef.current.scrollIntoView({ behavior: "smooth" });
            }
          }, 0);
        } else {
          setTimeout(() => {
            lastSeenLogRef.current?.scrollIntoView({ behavior: "auto" });
          }, 0);
        }
      })
      .finally(() => setLoading(false));
  };

  const setCurrentAction = (log: Log) => {
    setActiveAction({
      targetLogId: log.id,
      targetUserName: getActionTargetUserName(log),
      targetContent: log.content,
    });
  };

  const cancelAction = () => {
    setActiveAction(undefined);
  };

  const toggleReplies = (parentLogId: string) => {
    setExpandedReplyLogIds((prevState) => ({
      ...prevState,
      [parentLogId]: !prevState[parentLogId],
    }));
  };

  const onAddLog = () => {
    const nextContent = content.trim();
    const uploadedFiles = attachmentFiles.flatMap((file) =>file.response ? [file.response] : []);   

    if (!logBookId || (!nextContent && uploadedFiles.length === 0)) return;

    setAddLoading(true);

    createLogAttachmentDocument(uploadedFiles)
      .then((requestDoc) => {
        const payload = {
          content: nextContent || undefined,
          docId: requestDoc?.id,
        };
        const addLogRequest = activeAction
          ? logApis.logBookAddSubLog(activeAction.targetLogId, payload)
          : logApis.logBookAddLog(logBookId, payload);

        return addLogRequest.then((res) => {
        const nextLog = {
          ...res,
          creator: user,
          content: res.content ?? nextContent,
          docId: res.docId || requestDoc?.id || "",
          doc: res.doc ?? requestDoc,
          subs: res.subs ?? [],
        };

        setLogs((prevLogs) => {
          if (!activeAction) {
            return [...prevLogs, nextLog];
          }

          return prevLogs.map((currentLog) => {
            if (currentLog.id !== activeAction.targetLogId) {
              return currentLog;
            }

            const nextSubs = [...(currentLog.subs ?? []), nextLog].sort(
              (leftLog, rightLog) =>
                (leftLog.createdAt ?? 0) - (rightLog.createdAt ?? 0),
            );

            return {
              ...currentLog,
              subs: nextSubs,
            };
          });
        });

        if (activeAction) {
          setExpandedReplyLogIds((prevState) => ({
            ...prevState,
            [activeAction.targetLogId]: true,
          }));
        }

        setContent("");
        setAttachmentFiles([]);
        setActiveAction(undefined);

        if (!activeAction) {
          setTimeout(() => {
            if (lastLogRef.current) {
              lastLogRef.current.scrollIntoView({ behavior: "smooth" });
            }
          }, 0);
        }
      });
      })
      .finally(() => setAddLoading(false));
  };

  const actionPreviewLabel = activeAction
    ? t("components.replyTo", { name: activeAction.targetUserName })
    : "";
  const actionPreviewContent = activeAction?.targetContent ?? "";
  const cancelActionLabel = t("components.cancelReply");
  const attachmentUploading = attachmentFiles.some(
    (file) => file.status === "uploading",
  );
  const hasUploadedFile = attachmentFiles.some((file) => !!file.response);
  const canSend = !attachmentUploading && (!!content.trim() || hasUploadedFile);

  return (
    <LogWrap>
      {loading && offset !== 0 && (
        <Flex style={{ width: "100%" }} justify="center" align="center">
          <Spin />
        </Flex>
      )}
      {loading && offset === 0 && (
        <Flex
          style={{ width: "100%", height: "100%" }}
          justify="center"
          align="center"
        >
          <Spin />
        </Flex>
      )}

      <div
        className="log-list--wrap"
        style={{ overflowY: "auto" }}
        onScroll={(e) => {
          if (e.currentTarget.scrollTop === 0 && hasMore) {
            getLogList(offset);
          }
        }}
      >
        {logs.map((log, index) => (
          <div
            key={log.id}
            id={`log-${log.id}`}
            ref={index === logs.length - 1 ? lastLogRef : null}
          >
            <LogItem
              log={log}
              replyExpanded={!!expandedReplyLogIds[log.id]}
              onReply={setCurrentAction}
              onToggleReplies={toggleReplies}
            />
          </div>
        ))}
      </div>

      <div className="log-create--wrap"
        onDragOver={(event) => {
          // 只接收文件拖拽，保留输入框原有的文本拖拽行为。
          if (!Array.from(event.dataTransfer.types).includes("Files")) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = addLoading ? "none" : "copy";
        }}
        onDrop={(event) => {
          // Upload 内部处理 drop 时会先 preventDefault，外层跳过以免重复上传。
          if (event.defaultPrevented || !event.dataTransfer.files.length) return;
          event.preventDefault();
          event.stopPropagation();
          attachmentUploadRef.current?.addFiles(event.dataTransfer.files);
        }}
      >
        {activeAction && (
          <div className="log-action-preview">
            <div className="log-action-preview__main">
              <div className="log-action-preview__text">
                {actionPreviewLabel}
              </div>
              <div className="log-action-preview__content">
                {actionPreviewContent}
              </div>
            </div>
            <button
              type="button"
              className="log-action-preview__close"
              onClick={cancelAction}
              aria-label={cancelActionLabel}
              title={cancelActionLabel}
            >
              ×
            </button>
          </div>
        )}

        <LogAttachments
          files={attachmentFiles}
          disabled={addLoading}
          onRemove={(uid) =>
            setAttachmentFiles((files) =>
              files.filter((file) => file.uid !== uid),
            )
          }
        />

        <Input.TextArea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          disabled={addLoading}
          placeholder={t("components.plContent")}
          autoSize={{ minRows: 4, maxRows: 6 }}
          variant="borderless"
          maxLength={MAX_LOG_CONTENT_LENGTH}
        />

        <div className="log-options">
          <LogAttachmentUpload
            ref={attachmentUploadRef}
            fileList={attachmentFiles}
            disabled={addLoading}
            onChange={setAttachmentFiles}
          />
          <div className="log-options__actions">
            <span className="log-content-count">
              {content.length} / {MAX_LOG_CONTENT_LENGTH}
            </span>
            <Button
              size="small"
              type="primary"
              onClick={onAddLog}
              loading={addLoading}
              disabled={addLoading || !canSend}
            >
              {t("components.send")}
            </Button>
          </div>
        </div>
      </div>
    </LogWrap>
  );
};

export default LogHome;
